import React, { useCallback } from "react";
import { FieldValues, useController } from "react-hook-form";

import { IStringOptionSelectProps, StringOptionSelect } from "../input/StringOptionSelect";
import { FieldPathOfType, IControllerProps } from "./formTypes";

type IControlledStringOptionSelectProps<T extends string> = Omit<IStringOptionSelectProps<T>, "value">;

interface IProps<
    TOption extends string,
    TFieldValues extends FieldValues,
    TName extends FieldPathOfType<TFieldValues, TOption>,
> extends IControlledStringOptionSelectProps<TOption> {
    name: TName;
    controllerProps?: IControllerProps<TFieldValues, TName, TOption>;
}

export function ControllerStringOptionSelect<
    TOption extends string = string,
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathOfType<TFieldValues, TOption> = FieldPathOfType<TFieldValues, TOption>,
>({
    name,
    controllerProps,
    onChange,
    onBlur,
    ...stringOptionSelectProps
}: IProps<TOption, TFieldValues, TName>): React.ReactElement | null {
    const {
        field: { value, onChange: onFieldChange, onBlur: onFieldBlur, ref },
    } = useController({
        name,
        ...controllerProps,
        rules: {
            pattern: {
                value: regexFromStrings(stringOptionSelectProps.options.map(({ value: optionValue }) => optionValue)),
                message: "Invalid option",
            },
            ...controllerProps?.rules,
        },
    });

    const handleChange = useCallback(
        (option: TOption | undefined): void => {
            onFieldChange(option ?? null);
            onChange?.(option as never);
        },
        [onFieldChange, onChange],
    );
    const handleBlur = useCallback((): void => {
        onFieldBlur();
        onBlur?.();
    }, [onFieldBlur, onBlur]);

    return (
        <MemoizedStringOptionSelect<TOption>
            {...stringOptionSelectProps}
            ref={ref}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
}

const MemoizedStringOptionSelect = React.memo(StringOptionSelect) as typeof StringOptionSelect;

function regexFromStrings(values: string[]): RegExp {
    return new RegExp(
        values
            // Escape special characters.
            .map((value) => value.replace(/[()[\]{}*+?^$|#.,/\\\s-]/g, "\\$&"))
            // Sort for maximal munch.
            .sort((a, b) => b.length - a.length)
            .join("|"),
        "gi",
    );
}
